<template>
    <div
        :data-part="part.id"
        class="sentence__wrapper"
        :class="{ 'is-author': part.type_text.slug === 'author' }"
    >
        <transition name="fade">
            <SentenceControl
                v-if="!noControl && selector.isSentenceControlVisible(part.id)"
                :part="part"
                :selection="selector.sortedSelection"
                :config="controlConfig"
                @playPart="onPlayPart"
            />
        </transition>
        <!-- zero-width marker at the sentence start; SentenceControl anchors to it -->
        <span class="sentence__anchor" />
        <span
            :class="[
                'sentence',
                entityClass,
                {
                    'is-sample': part.is_sample_flag,
                    'is-active': isSelected,
                    'is-playing': isPlaying,
                    'sentence--suggestion': highlight && hasSuggestions,
                    'sentence--author': part.type_text.slug === 'author',
                },
            ]"
            :data-suggestions="hasSuggestions"
            @click.prevent.stop="
                leftClick({
                    id: part.id,
                    ctrl: $event.ctrlKey || $event.metaKey,
                    shift: $event.shiftKey,
                    immutable: part.immutable,
                    localId: part.local_id,
                    typeText: part.type_text.slug,
                    bookpart: part,
                })
            "
            @touchstart="onTouchStart()"
            @touchend="onTouchEnd(part)"
            @contextmenu.prevent.stop="
                rightClick({
                    id: part.id,
                    localId: part.local_id,
                    immutable: part.immutable,
                    typeText: part.type_text.slug,
                    bookpart: part,
                })
            "
        >
            <span
                v-if="isPlaying || part.is_sample_flag"
                class="sentence__badge"
                :class="{
                    'sentence__badge--sample': part.is_sample_flag,
                    'sentence__badge--nowplaying': isPlaying,
                }"
            >
                {{ isPlaying ? 'playing' : 'sample' }}
            </span>
            <span
                v-if="part.corrections && part.corrections.length"
                class="sentence__correction"
                :class="{
                    'sentence__correction--active': hasActiveCorrection,
                }"
            >
                ●
            </span>
            <template v-if="typeof rawText === 'string'">
                <span class="sentence__str" v-text="rawText" />
            </template>
            <template v-else>
                <span
                    v-for="(item, i) in rawText"
                    :key="i"
                    v-tooltip="activeStyleRule"
                    :class="['sentence__str', { 'sentence__str--rule': item.hasStyleRule }]"
                    @mouseover="onStyleRuleHover(item.ruleId)"
                    v-text="item.str"
                />
            </template>
        </span>
    </div>
</template>

<script>
import { fetchStylesheetItem } from '../../api/stylesheet';
import SentenceControl from '../SentenceControl/SentenceControl';
import { getConfig, actionMethodMap } from './utils';
import { mapGetters, mapActions } from 'vuex';

export default {
    name: 'Sentence',
    components: {
        SentenceControl,
    },

    inject: ['selector'],

    props: {
        part: {
            type: Object,
            required: true,
        },
        playingPart: {
            type: Object,
            default: () => undefined,
        },
        noControl: {
            type: Boolean,
            default: false,
        },
        showStyleRules: {
            type: Boolean,
            defaut: false,
        },
    },

    data() {
        return {
            loading: false,
            loadingStyleRule: false,
            styleRules: [],
            selectedStyleRule: undefined,
            touchStart: null,
        };
    },

    computed: {
        ...mapGetters(['isReviewer']),
        ...mapActions('bookpart', ['selectBookpart']),

        entityClass() {
            let classString = `sentence--${this.part.type_text.slug}`;
            return {
                [classString]: true,
            };
        },

        role() {
            return this.isReviewer ? 'reviewer' : '';
        },

        controlConfig() {
            return getConfig(this.role);
        },

        highlight() {
            return JSON.parse(localStorage.getItem('suggestions-highlight'));
        },

        isPlaying() {
            if (!!this.playingPart && this.part.id === this.playingPart.partId) {
                return true;
            }

            return false;
        },

        hasSuggestions() {
            return this.part.suggestions && this.part.suggestions.includes(this.part.id);
        },

        isSelected() {
            return this.selector.isSelected(this.part.id);
        },

        hasActiveCorrection() {
            return (
                Array.isArray(this.part.corrections) &&
                this.part.corrections.some((correction) => !!correction.need_redirect)
            );
        },

        rawText() {
            const hasStyleRule =
                this.showStyleRules && this.part.style_rules && this.part.style_rules.length;
            let text = (this.part.raw_text && this.part.raw_text.trim()) || '';

            if (!hasStyleRule) return text;

            if (text && hasStyleRule) {
                text = text.split(' ').map((str) => {
                    const item = { str: str + ' ' };
                    const ruleIdx = this.part.style_rules.findIndex(({ target }) => {
                        const regex = new RegExp(target, 'gi');
                        return regex.test(item.str);
                    });

                    if (ruleIdx >= 0 && this.showStyleRules) {
                        item.ruleId = this.part.style_rules[ruleIdx].rule_id;
                        item.hasStyleRule = true;
                    }

                    return item;
                });
            }

            return text;
        },

        activeStyleRule() {
            if (this.loadingStyleRule || !this.selectedStyleRule) return this.$t('misc.loading');
            return this.selectedStyleRule.explanation;
        },
    },

    created() {
        if (!this.selector) {
            throw new Error('Sentence component requires selector');
        }
    },

    methods: {
        rightClick(event) {
            this[actionMethodMap[this.controlConfig.rightClickAction]](event);
        },

        leftClick(event) {
            this[actionMethodMap[this.controlConfig.leftClickAction]](event);
            this.showStyleRule = false;
        },

        select(event) {
            this.selector.select(event);
        },

        openControl(event) {
            this.selector.openSentenceControl(event.id);
        },

        onPlayPart(event) {
            this.$emit('playPart', event);
        },

        onTouchStart() {
            this.touchStart = new Date().getTime();
        },

        onTouchEnd(part) {
            let now = new Date().getTime();

            if (now - this.touchStart > 300) {
                this.rightClick({
                    id: part.id,
                    localId: part.local_id,
                    immutable: part.immutable,
                    typeText: part.type_text.slug,
                    bookpart: part,
                });
            }
        },

        async onStyleRuleHover(id) {
            let item = this.styleRules.find((i) => i.id === id);

            if (!id) return;

            if (item) {
                this.selectedStyleRule = item;
                return;
            }

            this.loadingStyleRule = true;
            try {
                item = await fetchStylesheetItem(id);
                this.styleRules.push(item);
                this.selectedStyleRule = item;
            } catch (err) {
                console.log(err);
            } finally {
                this.loadingStyleRule = false;
            }
        },
    },
};
</script>

<style lang="scss" src="./Sentence.scss"></style>
